// Client-side CSV for the read-only statements. Built from the already-fetched
// `FinanceStatementResponse`, so no workbook endpoint is needed. Money is
// converted from integer minor units (cents) to a fixed two-decimal string
// with integer arithmetic only — no floating point on the amounts.
import type { FinanceStatementResponse } from "@/lib/api";
import { isCalculatedStatementLine } from "./pnl-summary";
import { computeChange, formatPercent, higherIsBetter } from "./variance";

/** `1234` -> `12.34`, `-5` -> `-0.05`; exact, integer-only conversion. */
export function minorToDecimal(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "";
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  const whole = Math.trunc(abs / 100);
  const fraction = abs % 100;
  return `${sign}${whole}.${String(fraction).padStart(2, "0")}`;
}

function escapeCell(value: string | number | null | undefined): string {
  const text = value === null || value === undefined ? "" : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function row(cells: Array<string | number | null | undefined>): string {
  return cells.map(escapeCell).join(",");
}

const BLANK = "";

/** A profit & loss or balance sheet as CSV (sections, rows, subtotals). */
export function statementToCsv(
  statement: FinanceStatementResponse,
  grandTotalLabel = "Total",
): string {
  const comparative =
    Array.isArray(statement.columns) && statement.columns.length > 0;
  const columns = comparative ? (statement.columns ?? []) : ["Amount"];
  const lines: string[] = [];
  lines.push(row(["Account", ...columns, ...(comparative ? ["Change", "Change %"] : [])]));

  for (const section of statement.sections) {
    const calculated = isCalculatedStatementLine(section.label);
    if (!calculated) lines.push(row([section.label]));
    for (const item of section.rows) {
      const amounts = comparative ? (item.amounts_cents ?? []) : [item.amount_cents];
      const cells = [
        `${item.code ? `${item.code} ` : ""}${item.name}`.trim(),
        ...amounts.map(minorToDecimal),
      ];
      if (comparative) {
        const change = computeChange(amounts[0], amounts[1], {
          favorableWhenUp: higherIsBetter(section.label),
        });
        cells.push(
          change ? minorToDecimal(change.absoluteCents) : BLANK,
          change ? formatPercent(change.percent) : BLANK,
        );
      }
      lines.push(row(cells));
    }
    const subtotals = comparative
      ? (section.subtotals_cents ?? [])
      : [section.subtotal_cents];
    const cells = [
      calculated ? section.label : `Total ${section.label}`,
      ...subtotals.map(minorToDecimal),
    ];
    if (comparative) {
      const change = computeChange(subtotals[0], subtotals[1], {
        favorableWhenUp: higherIsBetter(section.label),
      });
      cells.push(
        change ? minorToDecimal(change.absoluteCents) : BLANK,
        change ? formatPercent(change.percent) : BLANK,
      );
    }
    lines.push(row(cells));
  }

  const hasGrand =
    statement.grand_total_cents !== null &&
    statement.grand_total_cents !== undefined;
  const hasGrandTotals = (statement.grand_totals_cents?.length ?? 0) > 0;
  if (hasGrand || hasGrandTotals) {
    const totals = comparative
      ? (statement.grand_totals_cents ?? [])
      : [statement.grand_total_cents];
    const cells = [grandTotalLabel, ...totals.map(minorToDecimal)];
    if (comparative) {
      const change = computeChange(totals[0], totals[1]);
      cells.push(
        change ? minorToDecimal(change.absoluteCents) : BLANK,
        change ? formatPercent(change.percent) : BLANK,
      );
    }
    lines.push(row(cells));
  }
  return `${lines.join("\n")}\n`;
}

/** A trial balance as CSV (debit/credit legs per column, then totals). */
export function trialBalanceToCsv(statement: FinanceStatementResponse): string {
  const comparative =
    Array.isArray(statement.columns) && statement.columns.length > 0;
  const columns = comparative ? (statement.columns ?? []) : [];
  const lines: string[] = [];
  lines.push(
    row([
      "Account",
      ...(comparative
        ? columns.flatMap((column) => [`${column} Debit`, `${column} Credit`])
        : ["Debit", "Credit"]),
    ]),
  );

  for (const section of statement.sections) {
    lines.push(row([section.label]));
    for (const item of section.rows) {
      const cells = [`${item.code ? `${item.code} ` : ""}${item.name}`.trim()];
      if (comparative) {
        columns.forEach((_column, index) => {
          cells.push(
            minorToDecimal(item.debits_cents?.[index]),
            minorToDecimal(item.credits_cents?.[index]),
          );
        });
      } else {
        cells.push(minorToDecimal(item.debit_cents), minorToDecimal(item.credit_cents));
      }
      lines.push(row(cells));
    }
    const subtotalCells = [`Total ${section.label}`];
    if (comparative) {
      columns.forEach((_column, index) => {
        subtotalCells.push(
          minorToDecimal(section.debit_totals_cents?.[index]),
          minorToDecimal(section.credit_totals_cents?.[index]),
        );
      });
    } else {
      subtotalCells.push(
        minorToDecimal(section.debit_total_cents),
        minorToDecimal(section.credit_total_cents),
      );
    }
    lines.push(row(subtotalCells));
  }

  const columnCount = comparative ? columns.length : 1;
  const totals = Array.from({ length: columnCount }, (_, index) =>
    statement.sections.reduce(
      (sum, section) => ({
        debit:
          sum.debit +
          (section.debit_totals_cents?.[index] ??
            (comparative ? 0 : (section.debit_total_cents ?? 0))),
        credit:
          sum.credit +
          (section.credit_totals_cents?.[index] ??
            (comparative ? 0 : (section.credit_total_cents ?? 0))),
      }),
      { debit: 0, credit: 0 },
    ),
  );
  const totalCells = ["Total"];
  totals.forEach((total) => {
    totalCells.push(minorToDecimal(total.debit), minorToDecimal(total.credit));
  });
  lines.push(row(totalCells));
  return `${lines.join("\n")}\n`;
}

/** Saves client-built CSV text through a temporary object URL. */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
